import { RootState } from "@/redux/store";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import { useEffect, useMemo, useRef } from "react";
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { useLazyGetPriceMasterByIdQuery } from "@/services/priceMasterApi";
import { calculateItemPrice } from "@/lib/pricing-utils";
import { useGetMembershipsQuery } from "@/services/membershipsApi";
import { useGetAllProductsQuery } from "@/services/productApi";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { displayDate, formatToPKR } from "@/lib/helper";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import { BaseItem, InvoiceDetails } from "@/types/invoice";
import { POSProductType } from "@/types/enums";
import {
  Product,
  ProductStatus,
  ProductType,
} from "@/types/system-setting/product";
import { format } from "date-fns";
import Combobox from "@/components/ui/common/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SellableItem } from "@/types/pos/transaction";
import { calculateTaxFromTaxGroups } from "@/lib/tax-utils";
import { TaxGroup } from "@/types/system-setting/tax-group";
import { Membership } from "@/types/system-setting/membership";
import { SaleTax } from "@/types/system-setting/sale-tax";
import { MemberTableDatatypes } from "@/types/member";
import { useGetTaxGroupQuery, useLazyGetSalesTaxByIdQuery } from "@/services/salesTaxApi";

interface InvoiceHeaderProps {
  control: Control<InvoiceDetails>;
  errors: FieldErrors<InvoiceDetails>;
  watch: UseFormWatch<InvoiceDetails>;
  setValue: UseFormSetValue<InvoiceDetails>;
  action: string;
}

// Extended SellableItem for internal state (includes tax info for calculations)
type ExtendedInvoiceItem = SellableItem & {
  flexible_price?: boolean; // For wallet products with flexible pricing
  booking_date: string; // Required in invoices
  reset_nonce?: number; // Add this to force key change on reset
  item_tax_group?: TaxGroup | null; // Added for recalculation fallback
  discount_type?: "fixed" | "percentage";
  discount_percentage?: number; 
};

const initialInvoiceItem: ExtendedInvoiceItem = {
  item_id: 0,
  item_type: POSProductType.Membership,
  quantity: 0,
  discount: 0,
  booking_date: format(new Date(), "yyyy-MM-dd"),
  price: 0,
  flexible_price: false,
};

const InvoiceItemsDetails = ({
  control,
  errors,
  watch,
  setValue,
  action,
}: InvoiceHeaderProps) => {
  const watcher = watch();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: orgTaxTypeData } = useGetOrgTaxTypeQuery(orgId, {
    skip: !orgId,
  });
  const orgTaxType = orgTaxTypeData?.tax_type ?? "exclusive";
  const { data: membershipsData } = useGetMembershipsQuery({
    org_id: orgId,
    query: "status=active&type=default",
  });
  const { data: creditAddOnsData } = useGetMembershipsQuery({
    org_id: orgId,
    query: "status=active&type=credit_add_ons",
  });
  const { data: productsData } = useGetAllProductsQuery({
    org_id: orgId,
    query: "status=active&type=other_product",
  });
  const { data: memberList, refetch: refetchMemberList } = useGetAllMemberQuery(
    {
      org_id: orgId,
      query: "",
    },
  );

  const { data: taxGroupsData } = useGetTaxGroupQuery({ query: "", org_id: orgId }, {
    skip: !orgId,
  });

  const [getSalesTaxById] = useLazyGetSalesTaxByIdQuery();
  const [getPriceMasterById] = useLazyGetPriceMasterByIdQuery();

  const resolveTax = async (
    productTaxGroup: TaxGroup | null | undefined,
    memberTaxGroup: TaxGroup | null | undefined
  ) => {
    let { tax, taxRate, taxName } = calculateTaxFromTaxGroups(productTaxGroup, memberTaxGroup);

    if (!tax && productTaxGroup?.default_tax_id) {
      const inlineTax = productTaxGroup.taxes?.find((t: SaleTax) => t.id === productTaxGroup.default_tax_id);
      if (inlineTax) {
        return { taxRate: inlineTax.percentage, taxName: inlineTax.name, tax: inlineTax };
      }
      try {
        const defaultTaxResponse = await getSalesTaxById({ sale_tax_id: productTaxGroup.default_tax_id, orgId }).unwrap();
        if (defaultTaxResponse) {
          return { taxRate: defaultTaxResponse.percentage, taxName: defaultTaxResponse.name, tax: defaultTaxResponse };
        }
      } catch (err) {
        console.error("Failed to fetch default tax", err);
      }
    }

    return { taxRate, taxName, tax };
  };

  // Refetch member list when invoice opens with member-created action to ensure tax_group is available
  useEffect(() => {
    if (action === "member-created" && watcher.member_id) {
      // Small delay to ensure member creation is complete
      const timer = setTimeout(() => {
        refetchMemberList();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [action, watcher.member_id, refetchMemberList]);

  const membershipOptions = useMemo(() => {
    return Array.isArray(membershipsData?.data)
      ? membershipsData?.data
        .filter((membership) => membership.id !== undefined)
        .map((membership) => ({
          ...membership,
          value: membership.id!.toString(),
          label: membership.name,
        }))
      : [];
  }, [membershipsData]);

  const membershipComboboxOptions = useMemo(() => {
    if (!membershipOptions) return [];
    return membershipOptions
      .filter(
        (membership): membership is typeof membership & { id: number } =>
          membership.id !== undefined,
      )
      .map((membership) => ({
        value: membership.id.toString(),
        label: membership.name,
      }));
  }, [membershipOptions]);

  const creditAddOnOptions = useMemo(() => {
    return Array.isArray(creditAddOnsData?.data)
      ? creditAddOnsData?.data
        .filter((m) => m.id !== undefined)
        .map((m) => ({
          ...m,
          value: m.id!.toString(),
          label: m.name,
        }))
      : [];
  }, [creditAddOnsData]);

  const creditAddOnComboboxOptions = useMemo(() => {
    return (creditAddOnOptions || []).map((m) => ({
      value: m.id!.toString(),
      label: m.name,
    }));
  }, [creditAddOnOptions]);

  const productOptions = useMemo(() => {
    return Array.isArray(productsData?.data)
      ? productsData?.data
        .filter((product: Product) => product.status === ProductStatus.ACTIVE)
        .map((product: Product) => ({
          ...product,
          id: product.id,
          value: product.id?.toString() || "",
          label: product.product_name,
          net_price: product.price,
        }))
      : [];
  }, [productsData]);

  const productComboboxOptions = useMemo(() => {
    return productOptions?.map((product) => ({
      value: product.id?.toString() || "",
      label: product.product_name,
    }));
  }, [productOptions]);

  const [items, setItems] = useState<ExtendedInvoiceItem[]>([
    initialInvoiceItem,
  ]);
  const hasLoadedInitialData = useRef(false);
  const isInternalUpdate = useRef(false); // Track if update is from internal state changes
  const isHydratingFromForm = useRef(false); // Track initial hydration (should not mark form dirty)
  const prevMemberIdRef = useRef<number>(0);

  // If user changes/deselects member while items are selected, clear items (taxes can differ per member)
  useEffect(() => {
    const currentMemberId = Number(watcher.member_id || 0);
    const prevMemberId = prevMemberIdRef.current;
    const formItemsCount = Array.isArray(watcher.items)
      ? watcher.items.length
      : 0;

    // First render / initial state: just seed the ref
    if (prevMemberId === 0 && currentMemberId !== 0) {
      prevMemberIdRef.current = currentMemberId;
      return;
    }

    /**
     * IMPORTANT: Parent `reset(...)` (e.g. Update & Pay -> handleClose) will often set:
     * - member_id = 0
     * - items = []
     *
     * At that moment our local `items` state can still contain selected rows,
     * which would incorrectly trigger the "member changed/deselected" toast.
     * Treat this as a form reset and do nothing.
     */
    if (currentMemberId === 0 && formItemsCount === 0) {
      prevMemberIdRef.current = currentMemberId;
      return;
    }

    const memberChanged = prevMemberId !== currentMemberId;
    const hasSelectedItems = items.some((it) => (it.item_id || 0) > 0);

    // Avoid clearing during form hydration or internal state sync
    if (
      memberChanged &&
      hasSelectedItems &&
      !isHydratingFromForm.current &&
      !isInternalUpdate.current
    ) {
      isInternalUpdate.current = true;
      setItems([initialInvoiceItem]);

      toast({
        variant: "destructive",
        title: currentMemberId === 0 ? "Member deselected" : "Member changed",
        description:
          "Items have been cleared as member was changed. Taxes may differ for the new member.",
      });
    }

    prevMemberIdRef.current = currentMemberId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watcher.member_id, watcher.items]);

  // Get selected member's tax group
  const selectedMember = useMemo(() => {
    if (!watcher.member_id) return null;
    const member = memberList?.data?.find(
      (m: MemberTableDatatypes) => m.id === watcher.member_id,
    );
    if (member) return member;
    return {
      id: watcher.member_id,
      member_group_id: watcher.member_group_id,
    } as any as MemberTableDatatypes;
  }, [watcher.member_id, watcher.member_group_id, memberList]);

  // Get specific member tax group from the list based on watcher.member_tax_group_id
  const memberTaxGroup: TaxGroup | null = useMemo(() => {
    if (!watcher.member_tax_group_id || !taxGroupsData?.data) {
      // Fallback to member's default if no specific ID selected yet
      if (!selectedMember?.tax_group) return null;
      return (Array.isArray(selectedMember.tax_group)
        ? selectedMember.tax_group[0]
        : selectedMember.tax_group) as TaxGroup;
    }

    return taxGroupsData.data.find(
      (tg: TaxGroup) => tg.id === watcher.member_tax_group_id
    ) || null;
  }, [watcher.member_tax_group_id, taxGroupsData, selectedMember]);

  const addItem = () => {
    // Validate member is selected before adding items
    if (!watcher.member_id || watcher.member_id === 0) {
      toast({
        variant: "destructive",
        title: "Member Required",
        description: "Please select a member before adding items",
      });
      return;
    }

    const newItem: ExtendedInvoiceItem = {
      item_id: 0,
      item_type: POSProductType.Membership,
      quantity: 0,
      discount: 0,
      booking_date: format(new Date(), "yyyy-MM-dd"),
      price: 0,
      flexible_price: false,
    };
    isInternalUpdate.current = true;
    setItems((prev) => [...prev, newItem]);
  };

  const deleteItem = (id: number, type: POSProductType) => {
    isInternalUpdate.current = true;
    setItems(
      items.length === 1
        ? [initialInvoiceItem]
        : items.filter(
          (item) => !(item.item_id === id && item.item_type === type),
        ),
    );
  };

  // Helper function to calculate tax for an item
  const calculateItemTax = (
    item: ExtendedInvoiceItem,
    productTaxGroup: TaxGroup | null | undefined,
    taxResolution?: { tax: SaleTax | null, taxRate: number, taxName: string }
  ) => {
    const { tax, taxRate, taxName } = taxResolution || calculateTaxFromTaxGroups(
      productTaxGroup,
      memberTaxGroup,
    );

    const quantity = item.quantity || 0;
    const price = item.price || 0;
    const discount = item.discount || 0;

    // Calculate subtotal
    const subTotal = quantity * price;

    // Calculate taxable amount (after discount)
    const taxableAmount = Math.max(subTotal - discount, 0);

    let taxAmount = 0;
    let total = 0;
    let netPrice = 0;

    // A taxRate of -1 indicates a donation, which should not result in tax calculation
    if (taxRate > 0) {
      if (orgTaxType === "exclusive") {
        // For exclusive tax: Calculate tax on amount after discount
        taxAmount = (taxableAmount * taxRate) / 100;
        total = taxableAmount + taxAmount;
        netPrice = taxableAmount;
      } else if (orgTaxType === "inclusive") {
        // For inclusive tax: Extract tax from amount after discount
        taxAmount = taxableAmount - taxableAmount / (1 + taxRate / 100);
        total = taxableAmount;
        netPrice = taxableAmount - taxAmount;
      }
    } else {
      taxAmount = 0;
      total = taxableAmount;
      netPrice = taxableAmount;
    }

    return {
      taxRate: tax?.percentage === -1 ? -1 : Math.round(taxRate * 100) / 100,
      taxAmount: Math.round(taxAmount),
      total: Math.round(total),
      netPrice: Math.round(netPrice),
      taxName: taxName,
      taxId: tax?.id,
      isSrb: tax?.type === "srb",
      subTotal: Math.round(subTotal),
    };
  };

  const updateItem = async (
    id: number,
    type: POSProductType,
    field: keyof ExtendedInvoiceItem,
    value: string | number,
  ) => {
    const updatedItems = await Promise.all(items.map(async (item) => {
      if (item.item_id == id && item.item_type == type) {
        const updatedItem = {
          ...item,
          [field]: value,
          ...(field === "discount" && {
            discount_type: "fixed" as const,
            discount_percentage: 0,
          }),
        };

        // Extract fields with defaults for calculation
        const quantity = updatedItem.quantity || 0;
        const price = updatedItem.price || 0;
        let discount = updatedItem.discount || 0;

        // Calculate subtotal first
        const subTotal = quantity * price;

        // If updating discount, validate it against subtotal
        if (field === "discount" && Number(value) >= subTotal) {
          toast({
            variant: "destructive",
            title: "Discount should be less than net amount",
          });
          return item; // Return original item without updates
        }

        // Adjust discount based on quantity changes
        if (field === "quantity") {
          if (item.discount_type === "percentage" && item.discount_percentage) {
            const newSubTotal = quantity * price;
            discount = (newSubTotal * item.discount_percentage) / 100;
          }
        }

        // Get product/membership tax group and income category
        let productTaxGroup: TaxGroup | null = null;
        let incomeCategoryId: number | undefined =
          updatedItem.income_category_id;

        if (updatedItem.item_type === POSProductType.Membership || updatedItem.item_type === POSProductType.CreditAddOns) {
          const sourceOptions = updatedItem.item_type === POSProductType.CreditAddOns ? creditAddOnOptions : membershipOptions;
          const membershipData = sourceOptions?.find(
            (membership) => membership.id === updatedItem.item_id,
          ) as Membership | undefined;
          productTaxGroup = membershipData?.tax_group || null;
          // Set income category from membership if not already set
          if (!incomeCategoryId && membershipData?.income_category_id) {
            incomeCategoryId = membershipData.income_category_id;
          }
        } else if (updatedItem.item_type === POSProductType.Product) {
          const productData = productOptions?.find(
            (product) => product.id === updatedItem.item_id,
          ) as Product | undefined;
          productTaxGroup = productData?.tax_group || null;
          // Set income category from product if not already set
          if (!incomeCategoryId && productData?.income_category_id) {
            incomeCategoryId = Number(productData.income_category_id);
          }
        }

        // Calculate tax using resolveTax (handles default_tax_id and donation)
        const taxResolution = await resolveTax(productTaxGroup, memberTaxGroup);
        const taxCalculation = calculateItemTax(updatedItem, productTaxGroup, taxResolution);

        // Update fields in the item
        const returnItem: ExtendedInvoiceItem = {
          ...updatedItem,
          income_category_id: incomeCategoryId,
          sub_total: taxCalculation.subTotal,
          tax_rate: taxCalculation.taxRate,
          tax_amount: taxCalculation.taxAmount,
          total: taxCalculation.total,
          discount: discount,
          net_price: taxCalculation.netPrice,
          sale_tax_id: taxCalculation.taxId,
          tax_name: taxCalculation.taxName,
          tax_type: orgTaxType,
          is_srb: taxCalculation.isSrb,
          item_tax_group: productTaxGroup, // Store for recalculation
        };
        return returnItem;
      }
      return item;
    }));

    isInternalUpdate.current = true;
    setItems(updatedItems);
  };

  const handlePlanSelect = async (selectedItem: Product | Membership, index: number) => {
    // Validate member is selected before selecting products
    if (!watcher.member_id || watcher.member_id === 0) {
      toast({
        variant: "destructive",
        title: "Member Required",
        description: "Please select a member before selecting products",
      });
      return;
    }

    if (selectedItem) {
      const updatedItems = [...items];
      const currentItem = updatedItems[index];

      let name: string = "";
      let price: number;
      let discount: number;
      let income_category_id: number;
      let id: number;
      let item_type: POSProductType;
      let flexible_price: boolean = false;
      let productTaxGroup: TaxGroup | null = null;

      // Handle membership selection
      if (currentItem.item_type === POSProductType.Membership || currentItem.item_type === POSProductType.CreditAddOns) {
        const sourceOptions = currentItem.item_type === POSProductType.CreditAddOns ? creditAddOnOptions : membershipOptions;
        const membership = selectedItem as Membership;
        console.log("Full membership object:", JSON.stringify(membership));

        let basePrice = membership.net_price || (membership as any).price || 0;

        console.log("basePrice before price master:", basePrice);

        if (membership.price_master_id) {
          const pmData = await getPriceMasterById({
            id: membership.price_master_id,
            org_id: orgId,
          }).unwrap();

          const masterBasePrice = pmData.price ?? basePrice;

          const { price: bestPrice } = calculateItemPrice(masterBasePrice, {
            specialPricing: pmData.special_pricing?.map((sp: any) => ({
              name: sp.name,
              price: sp.price,
              date_from: sp.date_from || "",
              date_to: sp.date_to || "",
            })),
            planPricing: pmData.plan_pricing,
            groupPricing: pmData.group_pricing,
            member: selectedMember ?? undefined,
          });

          basePrice = bestPrice;
        }

        price = basePrice;
        discount = membership.discount ?? 0;
        income_category_id = membership.income_category_id || 0;
        id = membership.id as number;
        item_type = currentItem.item_type; // preserve CreditAddOns or Membership
        flexible_price = false;
        productTaxGroup = membership.tax_group || null;
        void sourceOptions; // suppress unused-variable warning
      }
      // Handle product selection
      else if (currentItem.item_type === POSProductType.Product) {
        const product = selectedItem as Product;
        name = product.product_name;
        // Check if product is a wallet with flexible pricing
        flexible_price =
          product.type === ProductType.Wallet &&
          product.flexible_price === true;
        let basePrice = flexible_price ? 0 : (product.price ?? 0);
        discount = 0;
        income_category_id = Number(product.income_category_id);
        id = product.id as number;
        item_type = POSProductType.Product;
        productTaxGroup = product.tax_group || null;
        if (!flexible_price && product.price_master_id) {
          try {
            const pmData = await getPriceMasterById({
              id: product.price_master_id,
              org_id: orgId,
            }).unwrap();

            if (pmData) {
              const masterBasePrice = pmData.price ?? basePrice;

              const { price: bestPrice } = calculateItemPrice(masterBasePrice, {
                specialPricing: pmData.special_pricing?.map((sp: any) => ({
                  name: sp.name,
                  price: sp.price,
                  date_from: sp.date_from || "",
                  date_to: sp.date_to || "",
                })),
                planPricing: pmData.plan_pricing,
                groupPricing: pmData.group_pricing,
                member: selectedMember ?? undefined,
                referenceDate: currentItem.booking_date || format(new Date(), "yyyy-MM-dd"),
              });
              basePrice = bestPrice;
            }
          } catch (e) {
            console.error("Failed to fetch price master for product", e);
          }
        }

        price = basePrice;
      } else {
        return; // Unknown type
      }

      // Create temporary item for tax calculation
      const tempItem: ExtendedInvoiceItem = {
        item_id: id,
        item_type: item_type,
        quantity: 1,
        price: price,
        discount: 0, // Will calculate discount amount after
        booking_date:
          currentItem.booking_date || format(new Date(), "yyyy-MM-dd"),
        flexible_price: flexible_price,
      };

      // Calculate discount amount (membership discount is percentage)
      const subTotal = price;
      const discountAmount =
        item_type === POSProductType.Membership
          ? (subTotal * discount) / 100
          : 0;

      // Update temp item with discount for tax calculation
      tempItem.discount = discountAmount;

      // Calculate tax using resolveTax (handles default_tax_id and donation)
      const taxResolution = await resolveTax(productTaxGroup, memberTaxGroup);
      const taxCalculation = calculateItemTax(tempItem, productTaxGroup, taxResolution);

      updatedItems[index] = {
        ...updatedItems[index],
        item_id: id,
        item_type: item_type,
        description: name,
        price: price,
        quantity: 1,
        discount: discountAmount,
        discount_type: item_type === POSProductType.Membership ? "percentage" : "fixed",
        discount_percentage: item_type === POSProductType.Membership ? discount : 0,
        booking_date: tempItem.booking_date,
        income_category_id: income_category_id,
        sub_total: taxCalculation.subTotal,
        net_price: taxCalculation.netPrice,
        tax_type: orgTaxType,
        tax_rate: taxCalculation.taxRate,
        tax_name: taxCalculation.taxName,
        tax_amount: taxCalculation.taxAmount,
        total: taxCalculation.total,
        sale_tax_id: taxCalculation.taxId,
        is_srb: taxCalculation.isSrb,
        flexible_price: flexible_price,
        item_tax_group: productTaxGroup, // Store for recalculation
      };
      isInternalUpdate.current = true;
      setItems([...updatedItems]);
    }
  };

  useEffect(() => {
    // Only update form values if this is an internal update (not from form reset)
    if (isInternalUpdate.current && items && items.length > 0) {
      // Transform items to BaseItem structure (includes flexible_price for validation)
      const minimalItems: BaseItem[] = items
        .filter((item) => item.item_id > 0) // Only include items with valid item_id
        .map((item) => {
          const minimal: BaseItem = {
            item_id: item.item_id,
            item_type: item.item_type,
            quantity: item.quantity,
            discount: item.discount,
            contract_id: item.contract_id,
            booking_date: item.booking_date,
            flexible_price: item.flexible_price, // Include flexible_price for validation
          };
          // Always include price for flexible price wallet products (even if 0) so validation can catch it
          if (item.flexible_price) {
            minimal.price = item.price ?? 0;
          }
          return minimal;
        });

      // Only mark dirty if this was a user-driven update (not initial hydration from edit/reset)
      const shouldDirty = !isHydratingFromForm.current;
      setValue("items", minimalItems, { shouldDirty });

      // NOTE: `sub_total` is already (quantity * price). Do NOT multiply by quantity again.
      const subtotal = items.reduce((acc, prod) => {
        const qty = prod.quantity || 0;
        const unitPrice = prod.price || 0;
        const lineSubTotal =
          prod.sub_total !== undefined && prod.sub_total !== null
            ? prod.sub_total
            : qty * unitPrice;
        return acc + (lineSubTotal || 0);
      }, 0);

      const totalTax = items.reduce(
        (acc, prod) => acc + (prod.tax_amount || 0),
        0,
      );

      const discountAmt = items.reduce(
        (acc, prod) => acc + (prod.discount || 0),
        0,
      );

      const total = items.reduce((acc, prod) => acc + (prod.total || 0), 0);

      setValue("sub_total", subtotal, { shouldDirty });
      setValue("tax_amount", totalTax, { shouldDirty });
      setValue("discount", discountAmt, { shouldDirty });
      setValue("total", total, { shouldDirty });

      // Reset the flag after a small delay to ensure other useEffects don't interfere
      setTimeout(() => {
        isInternalUpdate.current = false;
        isHydratingFromForm.current = false;
      }, 0);
    }
  }, [items, setValue]);

  // Reset hasLoadedInitialData when action changes
  useEffect(() => {
    hasLoadedInitialData.current = false;
    isInternalUpdate.current = false;
    /**
     * When the parent form changes `action`, it often immediately calls `reset(...)`
     * (e.g. after Save/Pay -> close). That reset can flip `member_id` (often to 0),
     * which should NOT be treated as a user-driven "member changed/deselected" event.
     *
     * Mark as hydrating briefly so the member-change effect doesn't clear items / toast.
     */
    isHydratingFromForm.current = true;
    prevMemberIdRef.current = Number(watcher.member_id || 0);
  }, [action]);

  // Recalculate cart when member tax group changes
  useEffect(() => {
    if (items.length > 0) {
      const recalculate = async () => {
        const updatedItems = await Promise.all(items.map(async (item) => {
          if (item.item_id > 0) {
            // Use the stored item_tax_group for recalculation
            const taxResolution = await resolveTax(item.item_tax_group, memberTaxGroup);
            const taxCalculation = calculateItemTax(item, item.item_tax_group, taxResolution);

            return {
              ...item,
              tax_rate: taxCalculation.taxRate,
              tax_name: taxCalculation.taxName,
              tax_amount: taxCalculation.taxAmount,
              total: taxCalculation.total,
              net_price: taxCalculation.netPrice,
              sale_tax_id: taxCalculation.taxId,
              is_srb: taxCalculation.isSrb,
            };
          }
          return item;
        }));

        isInternalUpdate.current = true;
        setItems(updatedItems);
      };
      recalculate();
    }
  }, [memberTaxGroup]);

  // Only load items from form when action changes to edit/member-created mode
  // Don't reset when watcher.items changes during normal editing
  useEffect(() => {
    const hydrate = async () => {
      // Skip if this is an internal update to prevent loops
      if (isInternalUpdate.current) {
        return;
      }

      if (action == "edit" || action == "member-created") {
        // Only load from watcher.items on initial load for this action
        // Wait for membership/product data to be available for enrichment
        if (watcher.items) {
          if (watcher.items.length > 0) {
            // Check if we already have these items in local state to avoid unnecessary re-hydrations
            const currentItemIds = items.map(i => `${i.item_id}-${i.item_type}-${i.quantity}`).join('|');
            const watcherItemIds = watcher.items.map(i => `${i.item_id}-${i.item_type}-${i.quantity}`).join('|');

            // If they are exactly the same and we've already loaded once, skip
            if (hasLoadedInitialData.current && currentItemIds === watcherItemIds) {
              return;
            }

            // Hydrating from form reset (edit/member-created) should not mark the form as dirty
            isHydratingFromForm.current = true;

            // Convert BaseItem[] to ExtendedInvoiceItem[] for internal state
            // For member-created action, enrich items with full membership/product data
            const extendedItems: ExtendedInvoiceItem[] = await Promise.all(watcher.items.map(
              async (item: BaseItem) => {
                const baseExtendedItem: ExtendedInvoiceItem = {
                  ...item,
                  flexible_price: item.flexible_price || false,
                  booking_date:
                    item.booking_date || format(new Date(), "yyyy-MM-dd"),
                  price: item.price || 0,
                };

                // Enrich items for both edit and member-created actions
                // For edit: ensure description matches combobox options and recalculate tax if needed
                // For member-created: enrich with full data if missing price/tax info
                if (item.item_id > 0) {
                  const hasCalculatedFields =
                    item.sub_total !== undefined &&
                    item.sub_total !== null &&
                    item.net_price !== undefined &&
                    item.net_price !== null &&
                    item.total !== undefined &&
                    item.total !== null &&
                    item.tax_amount !== undefined &&
                    item.tax_amount !== null &&
                    item.tax_rate !== undefined &&
                    item.tax_rate !== null;

                  const shouldRecalculate =
                    action === "member-created" || !hasCalculatedFields;

                  if (item.item_type === POSProductType.Membership) {
                    // Find the membership plan (ensure membershipOptions is available)
                    if (!membershipOptions || membershipOptions.length === 0) {
                      // Data not loaded yet, return item as-is
                      return baseExtendedItem;
                    }

                    const membership = membershipOptions.find(
                      (mp) => mp.id === item.item_id,
                    ) as Membership | undefined;

                    if (membership) {
                      // For edit mode: prefer existing discount amount; for member-created: compute from membership %
                      let discountAmount = item.discount || 0;
                      let itemPrice = item.price || membership.net_price;

                      if (action === "member-created") {
                        // For member-created: apply price master logic if available
                        let basePrice = membership.net_price || (membership as any).price || 0;

                        if (membership.price_master_id) {
                          try {
                            const pmData = await getPriceMasterById({
                              id: membership.price_master_id,
                              org_id: orgId,
                            }).unwrap();

                            const masterBasePrice = pmData.price ?? basePrice;

                            const { price: bestPrice } = calculateItemPrice(masterBasePrice, {
                              specialPricing: pmData.special_pricing?.map((sp: any) => ({
                                name: sp.name,
                                price: sp.price,
                                date_from: sp.date_from || "",
                                date_to: sp.date_to || "",
                              })),
                              planPricing: pmData.plan_pricing,
                              groupPricing: pmData.group_pricing,
                              member: selectedMember ?? undefined,
                            });

                            basePrice = bestPrice;
                          } catch (error) {
                            console.error("Error fetching price master during hydration:", error);
                          }
                        }

                        itemPrice = basePrice;
                        // Calculate discount amount (membership discount is percentage)
                        const discountPercentage = membership.discount || 0;
                        discountAmount = (itemPrice * discountPercentage) / 100;
                      }

                      // Create temp item for tax calculation
                      const tempItem: ExtendedInvoiceItem = {
                        item_id: membership.id as number,
                        item_type: POSProductType.Membership,
                        quantity: item.quantity || 1,
                        price: itemPrice,
                        discount: discountAmount,
                        booking_date:
                          item.booking_date || format(new Date(), "yyyy-MM-dd"),
                        flexible_price: false,
                      };

                      // Get product tax group
                      const productTaxGroup: TaxGroup | null =
                        membership.tax_group || null;

                      // Calculate tax using resolveTax when required
                      const itemPriceValue = tempItem.price ?? 0;
                      let taxCalculation = null;

                      if (shouldRecalculate) {
                        const taxResolution = await resolveTax(productTaxGroup, memberTaxGroup);
                        taxCalculation = calculateItemTax(tempItem, productTaxGroup, taxResolution);
                      }

                      // Return enriched item with correct description for combobox matching
                      return {
                        ...baseExtendedItem,
                        item_id: membership.id as number,
                        description: membership.name, // Ensure description matches combobox label
                        price: itemPrice,
                        quantity: item.quantity || 1,
                        discount: discountAmount,
                        income_category_id:
                          item.income_category_id ||
                          membership.income_category_id,
                        sub_total: shouldRecalculate
                          ? taxCalculation?.subTotal
                          : (item.sub_total ??
                            tempItem.quantity * itemPriceValue),
                        net_price: shouldRecalculate
                          ? taxCalculation?.netPrice
                          : (item.net_price ??
                            tempItem.quantity * itemPriceValue -
                            tempItem.discount),
                        tax_type: item.tax_type || orgTaxType,
                        tax_rate: shouldRecalculate
                          ? taxCalculation?.taxRate || 0
                          : (item.tax_rate ?? 0),
                        tax_name: shouldRecalculate
                          ? taxCalculation?.taxName || ""
                          : item.tax_name || "",
                        tax_amount: shouldRecalculate
                          ? taxCalculation?.taxAmount || 0
                          : (item.tax_amount ?? 0),
                        total: shouldRecalculate
                          ? taxCalculation?.total
                          : (item.total ??
                            tempItem.quantity * itemPriceValue -
                            tempItem.discount),
                        sale_tax_id: shouldRecalculate
                          ? taxCalculation?.taxId || item.sale_tax_id
                          : item.sale_tax_id,
                        is_srb: shouldRecalculate
                          ? taxCalculation?.isSrb || false
                          : item.is_srb || false,
                        contract_id: item.contract_id, // Preserve contract_id if present
                        item_tax_group: productTaxGroup, // Store for recalculation
                      };
                    }
                  } else if (item.item_type === POSProductType.Product) {
                    // Find the product (ensure productOptions is available)
                    if (!productOptions || productOptions.length === 0) {
                      // Data not loaded yet, return item as-is
                      return baseExtendedItem;
                    }

                    const product = productOptions.find(
                      (p) => p.id === item.item_id,
                    ) as Product | undefined;

                    if (product) {
                      const flexiblePrice =
                        product.type === ProductType.Wallet &&
                        product.flexible_price === true;

                      // For edit: keep existing price when present (especially flexible wallets); otherwise use product price
                      let itemPrice = item.price;
                      if (
                        itemPrice === undefined ||
                        itemPrice === null ||
                        (itemPrice === 0 && !flexiblePrice)
                      ) {
                        itemPrice = flexiblePrice ? 0 : (product.price as number);
                      }

                      // Create temp item for tax calculation
                      const tempItem: ExtendedInvoiceItem = {
                        item_id: product.id as number,
                        item_type: POSProductType.Product,
                        quantity: item.quantity || 1,
                        price: itemPrice,
                        discount: item.discount || 0,
                        booking_date:
                          item.booking_date || format(new Date(), "yyyy-MM-dd"),
                        flexible_price: flexiblePrice,
                      };

                      // Get product tax group
                      const productTaxGroup: TaxGroup | null =
                        product.tax_group || null;

                      // Calculate tax using resolveTax when required
                      const itemPriceValue = tempItem.price ?? 0;
                      let taxCalculation = null;

                      if (shouldRecalculate) {
                        const taxResolution = await resolveTax(productTaxGroup, memberTaxGroup);
                        taxCalculation = calculateItemTax(tempItem, productTaxGroup, taxResolution);
                      }

                      // Return enriched item with correct description for combobox matching
                      return {
                        ...baseExtendedItem,
                        item_id: product.id as number,
                        description: product.product_name, // Ensure description matches combobox label
                        price: itemPrice,
                        quantity: item.quantity || 1,
                        discount: item.discount || 0,
                        income_category_id:
                          item.income_category_id ||
                          Number(product.income_category_id),
                        sub_total: shouldRecalculate
                          ? taxCalculation?.subTotal
                          : (item.sub_total ??
                            tempItem.quantity * itemPriceValue),
                        net_price: shouldRecalculate
                          ? taxCalculation?.netPrice
                          : (item.net_price ??
                            tempItem.quantity * itemPriceValue -
                            tempItem.discount),
                        tax_type: item.tax_type || orgTaxType,
                        tax_rate: shouldRecalculate
                          ? taxCalculation?.taxRate || 0
                          : (item.tax_rate ?? 0),
                        tax_name: shouldRecalculate
                          ? taxCalculation?.taxName || ""
                          : item.tax_name || "",
                        tax_amount: shouldRecalculate
                          ? taxCalculation?.taxAmount || 0
                          : (item.tax_amount ?? 0),
                        total: shouldRecalculate
                          ? taxCalculation?.total
                          : (item.total ??
                            tempItem.quantity * itemPriceValue -
                            tempItem.discount),
                        sale_tax_id: shouldRecalculate
                          ? taxCalculation?.taxId || item.sale_tax_id
                          : item.sale_tax_id,
                        is_srb: shouldRecalculate
                          ? taxCalculation?.isSrb || false
                          : item.is_srb || false,
                        flexible_price: flexiblePrice,
                        contract_id: item.contract_id, // Preserve contract_id if present
                        item_tax_group: productTaxGroup, // Store for recalculation
                      };
                    }
                    } else if (item.item_type === POSProductType.CreditAddOns) {
                    if (!creditAddOnOptions || creditAddOnOptions.length === 0) {
                      return baseExtendedItem;
                    }

                    const addon = creditAddOnOptions.find(
                      (m) => m.id === item.item_id,
                    ) as Membership | undefined;

                    if (addon) {
                      let discountAmount = item.discount || 0;
                      let itemPrice = item.price || addon.net_price;

                      if (action === "member-created") {
                        const discountPercentage = addon.discount || 0;
                        discountAmount = (addon.net_price * discountPercentage) / 100;
                        itemPrice = addon.net_price;
                      }

                      const tempItem: ExtendedInvoiceItem = {
                        item_id: addon.id as number,
                        item_type: POSProductType.CreditAddOns,
                        quantity: item.quantity || 1,
                        price: itemPrice,
                        discount: discountAmount,
                        booking_date: item.booking_date || format(new Date(), "yyyy-MM-dd"),
                        flexible_price: false,
                      };

                      const productTaxGroup: TaxGroup | null = addon.tax_group || null;
                      const itemPriceValue = tempItem.price ?? 0;
                      let taxCalculation = null;

                      if (shouldRecalculate) {
                        const taxResolution = await resolveTax(productTaxGroup, memberTaxGroup);
                        taxCalculation = calculateItemTax(tempItem, productTaxGroup, taxResolution);
                      }

                      return {
                        ...baseExtendedItem,
                        item_id: addon.id as number,
                        description: addon.name,
                        price: itemPrice,
                        quantity: item.quantity || 1,
                        discount: discountAmount,
                        income_category_id: item.income_category_id || addon.income_category_id,
                        sub_total: shouldRecalculate ? taxCalculation?.subTotal : (item.sub_total ?? tempItem.quantity * itemPriceValue),
                        net_price: shouldRecalculate ? taxCalculation?.netPrice : (item.net_price ?? tempItem.quantity * itemPriceValue - tempItem.discount),
                        tax_type: item.tax_type || orgTaxType,
                        tax_rate: shouldRecalculate ? taxCalculation?.taxRate || 0 : (item.tax_rate ?? 0),
                        tax_name: shouldRecalculate ? taxCalculation?.taxName || "" : item.tax_name || "",
                        tax_amount: shouldRecalculate ? taxCalculation?.taxAmount || 0 : (item.tax_amount ?? 0),
                        total: shouldRecalculate ? taxCalculation?.total : (item.total ?? tempItem.quantity * itemPriceValue - tempItem.discount),
                        sale_tax_id: shouldRecalculate ? taxCalculation?.taxId || item.sale_tax_id : item.sale_tax_id,
                        is_srb: shouldRecalculate ? taxCalculation?.isSrb || false : item.is_srb || false,
                        contract_id: item.contract_id,
                        item_tax_group: productTaxGroup,
                      };
                    }
                  }
                }

                // Return item as-is if no enrichment needed
                return baseExtendedItem;
              },
            ));

            // Mark as internal update so form values get synced
            isInternalUpdate.current = true;
            setItems(extendedItems);
          } else {
            setItems([initialInvoiceItem]);
          }

          // Only mark as loaded if we have the required data for enrichment
          const hasRequiredData =
            membershipOptions &&
            membershipOptions.length > 0 &&
            productOptions &&
            productOptions.length > 0 &&
            creditAddOnOptions !== undefined;
          if (
            hasRequiredData ||
            watcher.items.every((item) => item.item_id === 0)
          ) {
            hasLoadedInitialData.current = true;
          }
        }
      } else if (action === "add") {
        // Only reset if we don't have valid items already
        const hasValidItems = items.some((item) => item.item_id > 0);
        if (!hasValidItems) {
          setItems([initialInvoiceItem]);
        }
      }
    };

    hydrate();
  }, [
    action,
    watcher.items,
    membershipOptions,
    productOptions,
    creditAddOnOptions,
    orgTaxType,
    memberTaxGroup,
  ]); // Watch dependencies to re-enrich when data loads or action changes

  return (
    <div className="h-fit rounded-2xl bg-white">
      <CustomCollapsible
        hideToggle={false}
        isOpenDefault={true}
        title="Products & Services"
      >
        <div className="overflow-x-auto">
          <table
            cellPadding="4"
            cellSpacing={2}
            className="w-full border-spacing-1 text-sm"
          >
            <thead>
              <tr className="text-left bg-gray-50 text-nowrap capitalize">
                <th className="p-2 font-semibold w-24">Type</th>
                <th className="p-2 font-semibold min-w-[150px]">Item</th>
                <th className="p-2 font-semibold w-28">Book date</th>
                <th className="p-2 font-semibold w-16">Qty</th>
                <th className="p-2 font-semibold w-20">Price</th>
                <th className="p-2 font-semibold w-20">Net</th>
                <th className="p-2 font-semibold w-20">Disc</th>
                <th className="p-2 font-semibold w-16">Tax%</th>
                <th className="p-2 font-semibold w-20">Tax Amt</th>
                <th className="p-2 font-semibold w-20">Total</th>
                <th className="p-2 font-semibold w-16">Action</th>
              </tr>
            </thead>

            <tbody className="text-nowrap">
              {items.map((item, index) => (
                <tr
                  key={`invoice-item-${index}-${item.item_type}-${item.item_id || "new"}`}
                  className="border-b"
                >
                  <td className="p-1">
                    <Select
                      value={item.item_type}
                      disabled={!watcher.member_id || watcher.member_id === 0}
                      onValueChange={(value) => {
                        // Validate member is selected before changing item type
                        if (!watcher.member_id || watcher.member_id === 0) {
                          toast({
                            variant: "destructive",
                            title: "Member Required",
                            description:
                              "Please select a member before adding items",
                          });
                          return;
                        }
                        isInternalUpdate.current = true;
                        const updatedItems = [...items];
                        updatedItems[index] = {
                          ...updatedItems[index],
                          item_type: value as POSProductType,
                          item_id: 0,
                          quantity: 0,
                          discount: 0,
                          price: 0,
                          flexible_price: false,
                        };
                        setItems(updatedItems);
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={POSProductType.Membership}>
                          Membership
                        </SelectItem>
                        <SelectItem value={POSProductType.Product}>
                          Product
                        </SelectItem>
                        <SelectItem value={POSProductType.CreditAddOns}>
                          Credit Add-ons
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-1">
                    <div className="flex items-center gap-2 relative">
                      <div className="flex-1 relative">
                        {(!watcher.member_id || watcher.member_id === 0) && (
                          <div
                            className="absolute inset-0 z-10 cursor-not-allowed"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toast({
                                variant: "destructive",
                                title: "Member Required",
                                description:
                                  "Please select a member before selecting products",
                              });
                            }}
                            title="Please select a member first"
                          />
                        )}
                        <Combobox
                          key={`combobox-${index}-${item.item_id}-${item.description || "no-desc"}-${item.reset_nonce || 0}`}
                          list={
                            item.item_type === POSProductType.Product
                              ? productComboboxOptions
                              : item.item_type === POSProductType.CreditAddOns
                              ? creditAddOnComboboxOptions
                              : membershipComboboxOptions
                          }
                          defaultValue={(() => {
                            // First try item.description (set by enrichment)
                            if (item.description) return item.description;

                            // Fallback: find the label from options based on item_id
                            if (item.item_id && item.item_id > 0) {
                              if (item.item_type === POSProductType.Product) {
                              const product = productOptions?.find(
                                (p) => p.id === item.item_id,
                              );
                              return product?.product_name || "";
                            } else if (item.item_type === POSProductType.CreditAddOns) {
                              const addon = creditAddOnOptions?.find(
                                (m) => m.id === item.item_id,
                              );
                              return addon?.name || "";
                            } else if (
                              item.item_type === POSProductType.Membership
                            ) {
                              const membership = membershipOptions?.find(
                                (m) => m.id === item.item_id,
                              );
                              return membership?.name || "";
                            }
                            }

                            return "";
                          })()}
                          label="Item"
                          showLabel={false}
                          className="text-xs h-8"
                          disabled={!watcher.member_id || watcher.member_id === 0}
                          setFilter={(selectedLabel: string) => {
                            // Validate member is selected before selecting products
                            if (!watcher.member_id || watcher.member_id === 0) {
                              toast({
                                variant: "destructive",
                                title: "Member Required",
                                description:
                                  "Please select a member before selecting products",
                              });
                              return;
                            }

                            if (!selectedLabel) {
                              // Clear selection
                              isInternalUpdate.current = true;
                              const updatedItems = [...items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                item_id: 0,
                                quantity: 0,
                                discount: 0,
                                price: 0,
                                flexible_price: false,
                              };
                              setItems(updatedItems);
                              return;
                            }

                            // Find the selected item based on label
                            let selectedItem: any = null;

                             if (item.item_type === POSProductType.Product) {
                              selectedItem = productOptions?.find(
                                (option: any) =>
                                  option.product_name === selectedLabel,
                              );
                            } else if (item.item_type === POSProductType.CreditAddOns) {
                              selectedItem = creditAddOnOptions?.find(
                                (option: any) => option.name === selectedLabel,
                              );
                            } else {
                              selectedItem = membershipOptions?.find(
                                (option: any) => option.name === selectedLabel,
                              );
                            }

                            if (!selectedItem) return;

                            // Check for duplicates using current item's type
                            if (
                              items.some(
                                (existingItem, idx) =>
                                  idx !== index &&
                                  existingItem.item_id == selectedItem.id &&
                                  existingItem.item_type === item.item_type,
                              )
                            ) {
                              toast({
                                variant: "destructive",
                                title: "Item is already selected",
                              });

                              // Reset the row to its initial (blank) state while maintaining the selected item_type
                              isInternalUpdate.current = true;
                              const updatedItems = [...items];
                              updatedItems[index] = {
                                ...initialInvoiceItem,
                                item_type: item.item_type,
                                description: "", // Explicitly clear description to ensure combobox resets
                                reset_nonce: Date.now(), // Force key change to remount Combobox
                              };
                              setItems(updatedItems);
                              return;
                            }

                            handlePlanSelect(selectedItem, index);
                          }}
                        />
                      </div>
                      {item.tax_rate === -1 && (
                        <span className="bg-primary text-black text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0">
                          Donation
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-1">
                    <Input
                      type="date"
                      className="h-8 text-xs px-1 invoice-booking-date w-full"
                      value={item.booking_date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value).getTime();
                        const currentDate = new Date().setHours(0, 0, 0, 0); // Start of today

                        if (selectedDate < currentDate) {
                          toast({
                            variant: "destructive",
                            title: "Validation Error",
                            description: "Date cannot be earlier than today",
                          });
                        } else {
                          item.item_id &&
                            updateItem(
                              item.item_id,
                              item.item_type,
                              "booking_date",
                              e.target.value,
                            );
                        }
                      }}
                      required
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      className="w-14 h-8 px-1 text-xs"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        item.item_id &&
                          updateItem(
                            item.item_id,
                            item.item_type,
                            "quantity",
                            Number(e.target.value),
                          );
                      }}
                      required
                    />
                  </td>
                  <td className="p-1">
                    {item.flexible_price ? (
                      <div>
                        <Input
                          className={`w-20 h-8 px-1 text-xs ${item.item_id && (!item.price || item.price <= 0) ? "border-red-500" : ""}`}
                          type="number"
                          value={item.price || ""}
                          onChange={(e) => {
                            const newPrice = Number(e.target.value);
                            if (newPrice < 0) {
                              toast({
                                variant: "destructive",
                                title: "Price cannot be negative",
                              });
                              return;
                            }
                            if (newPrice > 100000) {
                              toast({
                                variant: "destructive",
                                title: "Price limit exceeded",
                                description:
                                  "Wallet amount cannot exceed 1 lac (100,000)",
                              });
                              return;
                            }
                            item.item_id &&
                              updateItem(
                                item.item_id,
                                item.item_type,
                                "price",
                                newPrice,
                              );
                          }}
                          onBlur={(e) => {
                            // Validate on blur for flexible price products
                            if (item.item_id && item.flexible_price) {
                              const priceValue = Number(e.target.value);
                              if (!priceValue || priceValue <= 0) {
                                toast({
                                  variant: "destructive",
                                  title: "Price Required",
                                  description:
                                    "Please enter a valid price for this flexible pricing product",
                                });
                              }
                            }
                          }}
                          placeholder="Enter price *"
                          required
                          min={0}
                          max={100000}
                        />
                        {item.item_id && (!item.price || item.price <= 0) && (
                          <p className="text-red-500 text-[10px] mt-0.5">
                            Required
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs">
                        {formatToPKR(item.price || 0)}
                      </span>
                    )}
                  </td>
                  <td className="p-1 text-xs">
                    {formatToPKR(item.sub_total || 0)}
                  </td>
                  <td className="p-1">
                    <Input
                      className="w-16 h-8 px-1 text-xs"
                      type="number"
                      value={item.discount}
                      onChange={(e) =>
                        item.item_id &&
                        updateItem(
                          item.item_id,
                          item.item_type,
                          "discount",
                          Number(e.target.value),
                        )
                      }
                      required
                    />
                  </td>
                  <td className="p-1 text-xs text-center">
                    <div className="flex flex-col">
                      <span>{item.tax_rate === -1 ? 0 : (item.tax_rate || 0)}%</span>
                      {item.tax_name && (
                        <span className="text-[10px] text-gray-600 mt-0.5">
                          {item.tax_name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-1 text-xs">
                    {formatToPKR(item.tax_amount || 0)}
                  </td>
                  <td className="p-1 text-xs font-semibold">
                    {formatToPKR(item.total || 0)}
                  </td>
                  <td className="p-1">
                    <Button
                      onClick={() => deleteItem(item.item_id, item.item_type)}
                      variant="destructive"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Button
          onClick={addItem}
          variant="outline"
          className="mt-4"
          disabled={!watcher.member_id || watcher.member_id === 0}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Item
        </Button>
      </CustomCollapsible>
    </div>
  );
};

export default InvoiceItemsDetails;